import type { AICapability, QuickAction } from '../types/ai-assistant.types'

export const quickActions: QuickAction[] = [
  {
    id: 'optimize-pricing',
    label: 'Optimize Pricing',
    prompt:
      'Review my current pricing strategy and suggest optimizations based on usage patterns',
    icon: 'DollarSign',
    category: 'revenue',
  },
  {
    id: 'improve-margins',
    label: 'Improve Margins',
    prompt:
      'Show me opportunities to improve profit margins across my customer segments',
    icon: 'TrendingUp',
    category: 'margin',
  },
  {
    id: 'generate-report',
    label: 'Generate Value Reports',
    prompt: 'Create an executive summary of my agent value for a customer',
    icon: 'FileText',
    category: 'reports',
  },
  {
    id: 'find-revenue-leaks',
    label: 'Find Revenue Leaks',
    prompt:
      'Analyze my billing data and identify potential revenue leaks or unbilled usage',
    icon: 'TrendingDown',
    category: 'revenue',
  },

  {
    id: 'reduce-costs',
    label: 'Reduce Costs',
    prompt:
      'Where can I reduce operational costs in my AI agent infrastructure?',
    icon: 'ArrowDown',
    category: 'cost',
  },

  {
    id: 'setup-signals',
    label: 'Setup New Signals',
    prompt: 'Help me configure new tracking signals for my agents',
    icon: 'Settings',
    category: 'setup',
  },
]

export const aiCapabilities: AICapability[] = [
  {
    id: 'revenue-optimization',
    title: 'Revenue Optimization',
    description:
      'Maximize your revenue by identifying leaks, optimizing pricing, and discovering new monetization opportunities.',
    icon: 'TrendingUp',
    category: 'revenue',
    color: 'bg-green-100 text-green-700 border-green-200',
    quickActions: quickActions.filter(action => action.category === 'revenue'),
    examples: [
      'Find customers with high usage but low billing',
      'Suggest pricing tiers based on usage patterns',
      'Identify opportunities for premium features',
      'Analyze churn risk and retention strategies',
      'Optimize billing cycles and payment terms',
    ],
  },
  {
    id: 'value-reports',
    title: 'Value Reports',
    description:
      'Generate comprehensive reports showcasing value delivered, and business insights.',
    icon: 'FileText',
    category: 'reports',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    quickActions: quickActions.filter(action => action.category === 'reports'),
    examples: [
      'Executive summary dashboards',
      'Customer value delivery reports',
      'ROI and business impact analysis',
      'Custom metric visualizations',
    ],
  },
  {
    id: 'cost-management',
    title: 'Cost Analysis & Management',
    description:
      'Track, analyze, and optimize your operational costs across AI providers, infrastructure, and resources.',
    icon: 'Calculator',
    category: 'cost',
    color: 'bg-red-100 text-red-700 border-red-200',
    quickActions: quickActions.filter(action => action.category === 'cost'),
    examples: [
      'Analyze AI provider costs by model and usage',
      'Find the most expensive operations',
      'Compare provider pricing and suggest switches',
      'Track infrastructure spending trends',
      'Identify unused or underutilized resources',
    ],
  },
  {
    id: 'margin-analysis',
    title: 'Margin Improvement',
    description:
      'Understand your profit margins at every level and find opportunities to improve profitability.',
    icon: 'BarChart3',
    category: 'margin',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    quickActions: quickActions.filter(action => action.category === 'margin'),
    examples: [
      'Calculate margins by customer cohort',
      'Identify unprofitable agent actions',
      'Suggest pricing adjustments for better margins',
      'Track margin trends over time',
      'Compare margins across different agent actions',
    ],
  },
  {
    id: 'setup-onboarding',
    title: 'Setup & Configuration',
    description:
      'Get help setting up agents, configuring signals, and onboarding new customers with best practices.',
    icon: 'Settings',
    category: 'setup',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    quickActions: quickActions.filter(action => action.category === 'setup'),
    examples: [
      'Guide through signal configuration',
      'Set up billing meters and pricing',
      'Configure customer contracts',
      'Establish monitoring and alerts',
      'Create custom dashboards',
    ],
  },
  {
    id: 'contract-management',
    title: 'Custom Contracts',
    description: 'Create plans and entitlements from text contracts.',
    icon: 'FileText',
    category: 'setup',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    quickActions: quickActions.filter(action => action.category === 'reports'),
    examples: [
      'Analyze contract terms and conditions',
      'Cretae plans from text contract',
      'Create entitlements from text contract',
    ],
  },
]

export const welcomeMessage = {
  title: 'Meet Cooboy, your Agent Monetization Specialist',
  subtitle:
    'Access powerful tools to optimize billing, maximize revenue, and streamline your agent monetization strategy.',
  features: [
    'Analyze revenue leaks and optimization opportunities',
    'Track and reduce operational costs',
    'Improve profit margins across customer segments',
    'Generate insightful business reports',
    'Guide setup and configuration processes',
  ],
}
